import React, { useEffect, useCallback } from 'react';
import { embedDashboard } from '@superset-ui/embedded-sdk';
// import { dashboardId, getGuestToken, login, supersetUrl, getCookie } from './api';
// import { dashboardId, getGuestToken, login, supersetUrl } from './api';
// import styles from './App.module.css';
import useAuth from "./useAuth"; // Import the custom hook
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import "./style.css";
// import logo from "./logo_black.png";
import Header from './Header';
import ModuleHelp from './ModuleHelp';
// Same origin, like every other screen: nginx proxies /api and /auth to the backend on both
// ports, and an absolute http:// URL is blocked as mixed content the moment the site answers
// on https (2026-09-02). See backendConfig.js.
import { BACKEND_URL } from './backendConfig';

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://13.67.118.92:5000";


// The two messages are drawn by their own small components, so a language switch re-renders only them: the
// dashboard's embed effect below runs on every render of App, and re-embedding Superset for a change of
// language would sign it in again for nothing.
/* THE GUIDE (Max 01/11: every module has one). The screen is the Superset board alone — no title row of its own — so the
   Guide button floats at the bottom right instead of taking a strip of the board's height. */
const TOUR = [
  { selector: '#superset-container', title: 'tourUnitSales.boardT', body: 'tourUnitSales.boardB' },
  { selector: '#superset-container', title: 'tourUnitSales.useT', body: 'tourUnitSales.useB' },
];

const UnitSalesLoading = () => {
  const { t } = useTranslation();
  return <div>{t('xUnitSales.loading')}</div>;
};

const UnitSalesNotAuthorized = () => {
  const { t } = useTranslation();
  return <div>{t('xUnitSales.notAuthorized')}</div>;
};

const App = () => {
  const navigate = useNavigate();
  // const { auth, loading, name, logout } = useAuth();
  const { auth, loading, logout } = useAuth();

  // Define all styles as a static object
  // const styles = {
  //   // Global styles will be applied through the container hierarchy
  //   root: {
  //     margin: 0,
  //     padding: 0,
  //     width: '100%',
  //     height: '100%',
  //     overflow: 'hidden',
  //   },
  //   appContainer: {
  //     width: '100vw',
  //     height: '100vh',
  //     margin: 0,
  //     padding: 0,
  //     overflow: 'hidden',
  //     position: 'absolute',
  //     top: 0,
  //     left: 0,
  //     right: 0,
  //     bottom: 0,
  //     display: 'flex',
  //     flexDirection: 'column',
  //   },
  //   dashboardContainer: {
  //     width: '100vw',
  //     height: '100vh',
  //     margin: 0,
  //     padding: 0,
  //     boxSizing: 'border-box',
  //     overflow: 'hidden',
  //   },
  //   iframe: {
  //     minWidth: '100%',
  //     minHeight: '100%',
  //     border: 'none',
  //     display: 'block',
  //   }
  // };

  const styles = {
    appContainer: {
      width: '100vw',
      height: '100vh', 
      margin: 0,
      padding: 0,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden', // Only the container should be hidden
    },
    dashboardContainer: {
      width: '100%',
      height: 'calc(100vh - 60px)', // Subtracting header height
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
      overflow: 'auto', // Allow scrolling for the dashboard
    },
    iframe: {
      width: '100%',
      minHeight: '100%',
      border: 'none',
      display: 'block',
    }
  };

  const handleTokenExpiration = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // Function to check if session is valid
  const checkSession = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/`, { 
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error('Session invalid');
      }
      
      const data = await response.json();
      if (data.status !== "Success") {
        throw new Error('Session invalid');
      }
    } catch (error) {
      handleTokenExpiration();
    }
  }, [handleTokenExpiration]);

  const monitorIframeErrors = useCallback(() => {
    const iframe = document.querySelector('#superset-container iframe');
    if (iframe) {
      // Monitor iframe load events
      iframe.addEventListener('load', () => {
        if (iframe.contentDocument) {
          // Create observer for error messages
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Check for Superset error messages
              const errorElements = iframe.contentDocument?.querySelectorAll(
                '.error-message, .alert-danger, .alert-warning, ' +
                '[data-test="alert-warning"], [data-test="alert-danger"], ' +
                '.ant-alert-error, .ant-alert-warning'
              );
              
              errorElements?.forEach((element) => {
                const errorText = element.textContent?.toLowerCase() || '';
                if (
                  errorText.includes('error') ||
                  errorText.includes('unexpected') ||
                  errorText.includes('failed') ||
                  errorText.includes('unauthorized') ||
                  errorText.includes('token') ||
                  errorText.includes('authentication') ||
                  errorText.includes('session')
                ) {
                  checkSession();
                }
              });
            });
          });

          // Observe the entire iframe document for changes
          observer.observe(iframe.contentDocument.body, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true
          });

          return () => observer.disconnect();
        }
      });

      // Monitor iframe network errors
      iframe.addEventListener('error', () => {
        checkSession();
      });
    }
  }, [checkSession]);

  // Periodic session check
  useEffect(() => {
    if (auth) {
      const sessionCheck = setInterval(checkSession, 30000); // Check every 30 seconds
      return () => clearInterval(sessionCheck);
    }
  }, [auth, checkSession]);

  useEffect(() => {
    // Apply global styles
    document.documentElement.style.cssText = `
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
    `;
    document.body.style.cssText = `
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      overflow: hidden;
    `;

    // // Override fetch to force HTTP for Superset URLs
    // const originalFetch = window.fetch;
    // window.fetch = function(url, options) {
    //   if (typeof url === 'string' && url.includes('13.67.118.92:8088')) {
    //     url = url.replace('https://', 'http://');
    //   }
    //   return originalFetch(url, options);
    // };
    
    const authenticateAndEmbed = async () => {
      if (auth) {
        try {
          const accessResponse = await fetch(`${BACKEND_URL}/superset/login`);
          // const { access_token } = await accessResponse.json();
          if (!accessResponse.ok) {
            // throw new Error(`Login failed: ${accessResponse.status} ${accessResponse.statusText}`);
            throw new Error('Failed to get access token');
          }
          const { access_token } = await accessResponse.json();

          const guestResponse = await fetch(`${BACKEND_URL}/superset/guest-token?access_token=${access_token}`);
          // const { guest_token } = await guestResponse.json();

          if (!guestResponse.ok) {
            // throw new Error(`Guest token error: ${guestResponse.status} ${guestResponse.statusText}`);
            throw new Error('Failed to get guest token');
          }
          const { guest_token } = await guestResponse.json();

          const dashboardIdResponse = await fetch(`${BACKEND_URL}/superset/dashboardId`);
          const { SUP_DASHBOARD_ID } = await dashboardIdResponse.json();

          const supersetUrlResponse = await fetch(`${BACKEND_URL}/superset/supersetUrl`);
          const { SUP_URL } = await supersetUrlResponse.json();

          // // Force HTTP protocol
          // const forcedHttpUrl = SUP_URL.replace('https://', 'http://');
          // console.log('Using Superset URL:', forcedHttpUrl);         

          await embedDashboard({
            id: SUP_DASHBOARD_ID,
            supersetDomain: SUP_URL,
            // supersetDomain: forcedHttpUrl,
            // supersetDomain: window.location.origin + '/superset',  // This will use your app's domain
            mountPoint: document.getElementById('superset-container'),
            fetchGuestToken: () => Promise.resolve(guest_token),
            dashboardUiConfig: {
              hideTitle: true,
              cssOverrides: {
                dashboard: {
                  margin: 0,
                  padding: 0,
                  width: '100%',
                  height: '100%',
                },
              },
            },
            // enableCrossOriginIsolation: true,
            // targetOrigin: forcedHttpUrl,
            // onError: (error) => {
            //   console.error('Dashboard error:', error);
            //   if (error && error.message.includes('Token expired')) {
            //     alert('Session expired, redirecting to login.');
            //     navigate('/login');
            //   }
            // },
          });

          const iframe = document.querySelector('#superset-container iframe');
          if (iframe) {
            Object.assign(iframe.style, styles.iframe);
            monitorIframeErrors();
            // // Add a listener to monitor network requests within the iframe
            // const iframeWindow = iframe.contentWindow;
            // const iframeDocument = iframe.contentDocument || iframeWindow.document;

            // // Listen for network errors (for modern browsers with fetch)
            // iframeWindow.addEventListener('fetch', (event) => {
            //   event.respondWith(
            //     event.request.clone().json().then((response) => {
            //       if (response && response.status === 401) {
            //         alert('Token expired, redirecting to login.');
            //         navigate('/login');
            //       }
            //     })
            //   );
            // });
          }

          // const iframe = document.querySelector('#superset-container iframe');
          // if (iframe) {
          //   Object.assign(iframe.style, styles.iframe);
          //   iframe.onload = () => {
          //     console.log('Dashboard iframe loaded.');
          //   };
          //   iframe.onerror = () => {
          //     console.error('Dashboard iframe error.');
          //     alert('Session expired, redirecting to login.');
          //     navigate('/login');
          //   };
          // }
        } catch (error) {
          console.error('Error embedding dashboard:', error);
          // alert('Error loading dashboard, redirecting to login.');
          // navigate('/login');
          handleTokenExpiration();
        }
      }
    };

    authenticateAndEmbed();
  // }, [auth, navigate]);
  }, [logout, auth, handleTokenExpiration, monitorIframeErrors, styles.iframe]);

  if (loading) {
    return <UnitSalesLoading />;
  }

  if (!auth) {
    return <UnitSalesNotAuthorized />;
  }

  return (
    <div style={styles.appContainer}>
      <Header />
      <div className="content">
        <div id="superset-container" style={styles.dashboardContainer}></div>
        {/* above the Ask Catchy bubble, which owns the bottom-right corner */}
        <div style={{ position: 'fixed', right: 16, bottom: 88, zIndex: 50 }}>
          <ModuleHelp steps={TOUR} tourKey="productSales" ready={!loading && !!auth} />
        </div>
      </div>
      <style jsx="true">{`
        .content {
            flex: 1;
            display: flex;
            flex-direction: column;
            margin-top: 60px;
            padding-top: 0px;
            overflow: auto;
          }
      `}</style>
    </div>
  );
};

export default App;